import { IAdvanceLink, IBillingOrder, IDiscountLink } from "api/interfaces";
import {
  decimal,
  percentualToValue,
  subtract,
  sum,
} from "utils/DecimalPrecision";
import {
  EMeasurementStatus,
  EOrderStatus,
  ETechnicalRetentionType,
} from "../enums";
import ContractModel from "./ContractModel";

/**
 * Model de Medição
 * @category Measurements
 * @hideconstructor
 */

class MeasurementModel {
  /** ID da medição. */
  public readonly uid: string;
  /** ID do contrato que a medição pertence. */
  public readonly contractUid: string;
  /** Código sequencial da medição. */
  public readonly code: number;
  /** Descrição/título da medição. */
  public description: string;
  /** Data inicial do período da medição. */
  public initialDate: Date;
  /** Data final do período da medição. */
  public finalDate: Date;
  /** Total da medição. (Valor medido apenas) */
  public total: number;
  /** Valor do reajuste aplicado na medição */
  public readjustValue: number;
  /** Status manual da medição. */
  public status: EMeasurementStatus;
  /** Lista de parcelas de descontos vinculados a medição. */
  public discounts: IDiscountLink[];
  /** Lista de parcelas de descontos do tipo Faturamento Direto vinculados a medição. */
  public billings: IDiscountLink[];
  /** Lista de parcelas de adiantamentos vinculados a medição. */
  public advances: IAdvanceLink[];
  /** Objeto com dados do pedido de faturamento enviado para o Mega. */
  public billingOrder: IBillingOrder;
  /** Flag que sinaliza que a medição possui dados de envio para faturamento salvos. */
  public hasBillingData: boolean;
  /** IDs dos anexos da medição. */
  public attachments: string[];
  /** Data de criação do registro. */
  public readonly createdAt: Date;
  /** Data de última modificação do registro. */
  public modifiedAt: Date;

  /** Propriedade que aponta para o model do Contrato a qual a medição pertence,
   * pode ser utilizada para cacular valores com base nas configurações do projeto.
   */
  private _contract: ContractModel | undefined = undefined;

  constructor(
    uid: string,
    contractUid: string = "",
    code: number = 0,
    description: string = "",
    initialDate = new Date(),
    finalDate = new Date(),
    total: number = 0,
    readjustValue: number = 0,
    status: EMeasurementStatus = EMeasurementStatus.ANDAMENTO,
    discounts: IDiscountLink[] = [],
    billings: IDiscountLink[] = [],
    advances: IAdvanceLink[] = [],
    billingOrder?: IBillingOrder,
    hasBillingData: boolean = false,
    attachments: string[] = [],
    createdAt = new Date(),
    modifiedAt = new Date()
  ) {
    this.uid = uid;
    this.contractUid = contractUid;
    this.code = code;
    this.description = description;
    this.initialDate = initialDate;
    this.finalDate = finalDate;
    this.total = total;
    this.readjustValue = readjustValue;
    this.status = status;
    this.discounts = discounts;
    this.billings = billings;
    this.advances = advances;
    this.billingOrder = { ...defaultBillingOrder, ...billingOrder };
    this.hasBillingData = hasBillingData;
    this.attachments = attachments;
    this.createdAt = createdAt;
    this.modifiedAt = modifiedAt;
  }

  getContract(): ContractModel | undefined {
    return this._contract;
  }

  /** Soma o total medido com o reajuste e retorna o valor reajustado. */
  getReadjustedTotal() {
    return sum(this.total, this.readjustValue);
  }

  /** Calcula o percentual de reajuste da medição. */
  getReadjustedPercentage() {
    return decimal(this.readjustValue).dividedBy(this.total).toNumber();
  }

  /** Calcula o valor bruto da medição que será o valor da Nota Fiscal. */
  getGrossTotal(): number {
    return subtract(this.getReadjustedTotal(), this.getBillingsTotal());
  }

  /** Calcula o valor líquido da medição que será o valor total com todos os descontos. */
  getNetTotal(): number {
    return subtract(
      this.getReadjustedTotal(),
      this.getDiscountsTotal(),
      this.getBillingsTotal(),
      this.getAdvancesTotal(),
      this.getTaxesTotal(),
      this.getTechnicalRetentionTotal()
    );
  }

  /** Soma os valores de todas as parcelas de adiantamentos vinculados à medição. */
  getAdvancesTotal(): number {
    return sum(...this.advances.map((advance) => advance.value));
  }

  /** Soma os valores de todas as parcelas de descontos vinculados à medição. */
  getDiscountsTotal(): number {
    return sum(...this.discounts.map((discount) => discount.value));
  }

  /** Soma os valores de todas as parcelas de descontos do tipo Faturamento Direto
   * vinculados à medição. */
  getBillingsTotal(): number {
    return sum(...this.billings.map((billing) => billing.value));
  }

  /** Soma os valores de todas as parcelas de descontos de qualquer tipo vinculados
   * à medição.
   */
  getAllDiscountsTotal(): number {
    return sum(this.getDiscountsTotal(), this.getBillingsTotal());
  }

  /** Soma os valores de cada imposto aplicado ao total bruto da medição. */
  getTaxesTotal(): number {
    const taxes = this.getValuesOfEachTaxe();
    return sum(...Object.values(taxes));
  }

  /** Calcula o valor de cada imposto do contrato aplicado ao total bruto da medição. */
  getValuesOfEachTaxe() {
    const taxes = this._contract?.taxes;
    let values = {
      cofinsValue: 0,
      csllVaue: 0,
      inssValue: 0,
      issValue: 0,
      pisValue: 0,
    };

    if (taxes) {
      const total = this.getGrossTotal();
      values.cofinsValue = percentualToValue(taxes.cofins || 0, total, 2);
      values.csllVaue = percentualToValue(taxes.csll || 0, total, 2);
      values.inssValue = percentualToValue(taxes.inss || 0, total, 2);
      values.issValue = percentualToValue(taxes.iss || 0, total, 2);
      values.pisValue = percentualToValue(taxes.pis || 0, total, 2);
    }
    return values;
  }

  /** Calcula o valor da retenção técnica do contrato aplicado ao valor
   * total ou total bruto da medição.
   */
  getTechnicalRetentionTotal(): number {
    const technicalRetention = this._contract?.technicalRetention;
    if (!technicalRetention) return 0;

    return percentualToValue(
      technicalRetention.percentual || 0,
      technicalRetention.type === ETechnicalRetentionType.GROSSTOTAL
        ? this.getGrossTotal()
        : this.getReadjustedTotal(),
      2
    );
  }

  /** Retorna se a medição pode ser editada ou não, verificando se o status da mesma é
   * diferente de 'Faturada'.
   */
  canEdit(): boolean {
    return this.status !== EMeasurementStatus.FATURADA;
  }

  /** Retorna se a medição foi faturada no ERP Mega ou não, verificando se já possui um pedido de
   * faturamento vinculado à medição e que este não esteja com erro.
   */
  isBilled(): boolean {
    return this.hasBillingOrder() && !this.hasBillingOrderError();
  }

  /** Retorna se a medição já foi enviada para faturamento no ERP Mega ou não. */
  hasBeenSentToBilling(): boolean {
    return Boolean(this.billingOrder.sequencial);
  }

  /** Retorna se a medição está vinculada a um pedido no ERP Mega ou não. */
  hasBillingOrder(): boolean {
    return Boolean(this.billingOrder.code);
  }

  /** Retorna se o pedido vinculado a medição está com erro ou não. */
  hasBillingOrderError(): boolean {
    const status = this.billingOrder.status;
    return status === "error" || status === "canceled" || status === "deleted";
  }

  /** Retorna se o pedido vinculado a medição foi deletado do ERP Mega ou não. */
  hasBillingOrderDeleted(): boolean {
    return this.billingOrder.status === "deleted";
  }

  hasBillingOrderIssueDate() {
    return Boolean(this.billingOrder.orderIssueDate);
  }

  /** Vincula o model do contrato ao model da medição. */
  public attachContract(contract: ContractModel) {
    if (!contract || this.contractUid !== contract.uid) return;
    this._contract = contract;
  }

  /** Monta o objeto JSON com os dados que serão persistidos no Firebase. */
  toSave() {
    const data = {
      code: this.code,
      description: this.description,
      initialDate: this.initialDate,
      finalDate: this.finalDate,
      status: this.status,
      total: this.total,
      readjustValue: this.readjustValue,
      discounts: this.discounts,
      billing: this.billings,
      billingOrder: {},
      advances: [...this.advances],
      attachments: this.attachments,
      createdAt: this.createdAt,
      modifiedAt: this.modifiedAt,
    };

    if (this.billingOrder.code || this.billingOrder.sequencial)
      data.billingOrder = { ...this.billingOrder };

    return data;
  }

  /** Cria uma instância de MeasurementModel a partir do objeto JSON passado por parâmetro. */
  static create(
    uid: string,
    data: Partial<MeasurementModel>
  ): MeasurementModel {
    return new MeasurementModel(
      uid,
      data.contractUid,
      data.code,
      data.description,
      data.initialDate,
      data.finalDate,
      data.total,
      data.readjustValue,
      data.status,
      data.discounts,
      data.billings,
      data.advances,
      data.billingOrder,
      data.hasBillingData,
      data.attachments,
      data.createdAt,
      data.modifiedAt
    );
  }

  /** Cria uma medição em branco, passando apenas os campos obrigatórios. */
  static createBlankMeasurement(
    contractUid: string,
    code: number,
    description: string
  ) {
    return new MeasurementModel("", contractUid, code, description);
  }
}

const defaultBillingOrder = {
  branchId: 0,
  sequencial: 0,
  code: 0,
  organization: 0,
  serial: "",
  invoices: [],
  status: EOrderStatus.PROCESSING,
  error: "",
};

export default MeasurementModel;
